// Reconnecting WebSocket client. Adapted from gateway-dashboard/src/transport/ws.js
// (vanilla JS) into a typed handle. Parsing/validation is the caller's job —
// this layer only manages the socket lifecycle and reconnect with backoff.

export interface WsClientOptions {
  url: string | (() => string);
  onMessage?: (data: unknown, raw: MessageEvent) => void;
  onOpen?: (e: Event) => void;
  onClose?: (e: CloseEvent) => void;
  onError?: (e: unknown) => void;
  /** Parse text frames as JSON (default) or pass through raw. */
  parse?: 'json' | 'raw';
  reconnectMs?: number;
  maxReconnectMs?: number;
  WebSocketImpl?: typeof WebSocket;
}

export interface WsClient {
  connect: () => void;
  close: () => void;
  send: (data: unknown) => boolean;
  readonly connected: boolean;
}

const OPEN = 1;

export function createWsClient(opts: WsClientOptions): WsClient {
  const Impl = opts.WebSocketImpl || (typeof WebSocket !== 'undefined' ? WebSocket : null);
  const parse = opts.parse ?? 'json';
  const baseReconnect = opts.reconnectMs ?? 2000;
  const maxReconnect = opts.maxReconnectMs ?? 15000;

  let socket: WebSocket | null = null;
  let stopped = false;
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clearTimer() {
    if (timer) clearTimeout(timer);
    timer = null;
  }

  function scheduleReconnect() {
    if (stopped) return;
    clearTimer();
    // Exponential backoff with jitter to avoid thundering reconnect (SPEC §6).
    const delay = Math.min(baseReconnect * 2 ** attempts, maxReconnect);
    const jitter = Math.random() * baseReconnect;
    attempts += 1;
    timer = setTimeout(connect, delay + jitter);
  }

  function connect() {
    if (stopped) return;
    if (!Impl) {
      opts.onError?.(new Error('WebSocket not available'));
      scheduleReconnect();
      return;
    }
    let target: string;
    try {
      target = typeof opts.url === 'function' ? opts.url() : opts.url;
    } catch (err) {
      opts.onError?.(err);
      scheduleReconnect();
      return;
    }
    let sock: WebSocket;
    try {
      sock = new Impl(target);
    } catch (err) {
      opts.onError?.(err);
      scheduleReconnect();
      return;
    }
    socket = sock;
    sock.onopen = (e) => {
      attempts = 0;
      opts.onOpen?.(e);
    };
    sock.onmessage = (event) => {
      if (!opts.onMessage) return;
      if (parse === 'raw') {
        opts.onMessage(event.data, event);
        return;
      }
      // JSON: a malformed frame is dropped (caller never sees garbage).
      if (typeof event.data !== 'string') {
        opts.onMessage(event.data, event);
        return;
      }
      try {
        opts.onMessage(JSON.parse(event.data), event);
      } catch (err) {
        opts.onError?.(err);
      }
    };
    sock.onclose = (e) => {
      socket = null;
      opts.onClose?.(e);
      scheduleReconnect();
    };
    sock.onerror = (e) => {
      opts.onError?.(e);
      try {
        sock.close();
      } catch {
        /* ignore */
      }
    };
  }

  return {
    connect,
    close() {
      stopped = true;
      clearTimer();
      if (socket) {
        try {
          socket.onclose = null;
          socket.close();
        } catch {
          /* ignore */
        }
        socket = null;
      }
    },
    send(data: unknown): boolean {
      if (!socket || socket.readyState !== OPEN) return false;
      socket.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    },
    get connected() {
      return !!socket && socket.readyState === OPEN;
    },
  };
}
